import {getTasksInCardSelector} from 'stores/selectors/cardStateSelector';
import {useDispatch, useSelector} from 'react-redux';

import {doesCardHaveOverAspiratesTask} from 'lib/virtualTrayHelper';
import {getMaxAspirateHighVolume, getMaxAspirateVolume} from 'stores/selectors/configSelectors';
import {checkAndApplyGeneralTask} from 'stores/business/taskListBusinessAction';
import {getCardExecutionStatus} from 'stores/business/runtimeBusiness';
import {getLastOpenedProtocol} from 'stores/selectors/protocolSelectors';

import {TaskType, DragDropType, TrayType} from 'constants/dataEnum';
import {useDispatchAndAddUntoItem} from 'lib/customHooks';

import React, {useCallback, useMemo} from 'react';
import _, {debounce, orderBy} from 'lodash';

import PropTypes from 'prop-types';
import InsertTaskButton from './Tasks/InsertTaskButton';
import {
  disableSwapTaskPosition,
  onTaskIgnoreChange,
  onTaskItemDrop,
  swapTaskPosition,
} from './Tasks/simpleTransferTaskBusiness';
import SimpleTransferTasks from './Tasks/SimpleTransferTasks';

// TODO: handle drag and drop flow. make wrapper component for all tasks

const SimpleTransferTasksContainer = React.forwardRef(
  (
    {
      card,

      onSelectTask,
      onDelete,
      runtimeMode,
      readOnly,

      onArrowDownPress,
      onArrowUpPress,
      excludedComponentRef,
      onShowMenuContext,
      menuListRef,
      editablePickTipExcludedRef,
    },
    ref,
  ) => {
    const {ID: cardId, isInsertTask, invalidTypes} = card;

    const dispatch = useDispatch();
    const dispatchAddUndo = useDispatchAndAddUntoItem();

    const maxAspirateVolume = useSelector(getMaxAspirateVolume);
    const maxAspirateHighVolume = useSelector(getMaxAspirateHighVolume);
    const lastOpenedProtocol = useSelector(getLastOpenedProtocol);
    const tasks = useSelector(getTasksInCardSelector(card));

    const maxAspirateVolumeCaculated =
      lastOpenedProtocol?.trayType === TrayType.HIGH_VOLUME_CADDY ? maxAspirateHighVolume : maxAspirateVolume;

    const orderedTasks = useMemo(() => orderBy(tasks, ['number'], ['asc']), [tasks]);

    const taskEjectTip = orderedTasks.find((e) => e.type === TaskType.EJECT_TIP);
    const isMoreThanTipCapacity = doesCardHaveOverAspiratesTask(card, tasks, maxAspirateVolumeCaculated.value);
    const executionStatus = dispatch(getCardExecutionStatus(card));

    const swapPosition = useCallback(
      (item, targetItem) => {
        if (item.ID === targetItem) return;

        dispatch(swapTaskPosition(item.ID, targetItem, card));
      },
      [card, dispatch],
    );

    const onCheckedChange = (taskId, val) => dispatchAddUndo(onTaskIgnoreChange(cardId, taskId, val));
    const onApply = (task) => dispatchAddUndo(checkAndApplyGeneralTask(task, card));

    const onTaskDropped = () => dispatchAddUndo(onTaskItemDrop());
    const onTaskHovered = useCallback(
      (item, targetItem) => {
        // this item will be mutated by dragNDrop after this function is fired.
        // because we're using debounce, we would wanna keep the original item's index before its index is changed into the target item's index
        const itemBeforeMutatedByDnd = {...item};

        return debounce(() => swapPosition(itemBeforeMutatedByDnd, targetItem))();
      },
      [swapPosition],
    );

    const isDisableSwap = runtimeMode || dispatch(disableSwapTaskPosition(orderedTasks)) || readOnly;

    const draggableProps = {
      itemType: DragDropType.TASK_ITEM,
      onDrop: onTaskDropped,
    };

    const droppableProps = {
      onHover: onTaskHovered,
      waitUpdateItemPositionOnHoverCallback: true,
      acceptedItems: [DragDropType.TASK_ITEM],
      disableDrag: isDisableSwap,
    };

    const refProps = {
      excludedComponentRef,
      onShowMenuContext,
      menuListRef,
      editablePickTipExcludedRef,
    };

    const handleSelectTask = (taskId, otherProperties) => {
      const taskNumber = _.isNil(taskId) ? null : tasks[taskId].number;
      let taskList = [];

      if (taskNumber) {
        taskList = [tasks[taskId]];
      }
      onSelectTask({taskId, taskNumber, cardID: cardId, taskList, ...otherProperties});
    };

    return (
      <div ref={ref} className="card-simple-transfer" style={{width: '100%'}}>
        {orderedTasks.map((task, index) => (
          <>
            <SimpleTransferTasks
              itemID={index}
              key={task.ID}
              task={task}
              onSelectTask={handleSelectTask}
              onShowMenuContext={onShowMenuContext}
              onCheckedChange={onCheckedChange}
              onApply={onApply}
              onDelete={onDelete}
              onArrowDownPress={onArrowDownPress}
              onArrowUpPress={onArrowUpPress}
              ejectTipTasksExists={taskEjectTip}
              isMoreThanTipCapacity={isMoreThanTipCapacity}
              runtimeMode={runtimeMode}
              readOnly={readOnly}
              executionStatus={executionStatus}
              cardId={cardId}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...draggableProps}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...droppableProps}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...refProps}
            />
            {!readOnly && !runtimeMode && (
              <InsertTaskButton
                number={task.number}
                isInsertTaskShown={isInsertTask}
                runtimeMode={runtimeMode}
                isCardInvalid={invalidTypes?.length}
                cardId={cardId}
                onSelectTask={handleSelectTask}
              />
            )}
          </>
        ))}
      </div>
    );
  },
);

SimpleTransferTasksContainer.propTypes = {
  card: PropTypes.object.isRequired,

  onSelectTask: PropTypes.func.isRequired,
  onShowMenuContext: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,

  runtimeMode: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,

  runtimeStatus: PropTypes.string.isRequired,

  excludedComponentRef: PropTypes.array.isRequired,
  menuListRef: PropTypes.any.isRequired,
  editablePickTipExcludedRef: PropTypes.array.isRequired,
  onCloseEditablePickTip: PropTypes.func.isRequired,
};

export default SimpleTransferTasksContainer;
